import z from 'zod';
import { SharedGameSummarySchema } from '../games/games.schema';
import { Currency } from '../types/Currency.types';
import { PaginationParamsSchema } from '../types/Pagination.types';

export const GameLastWinDtoSchema = z.object({
  lastWinAt: z.string().nullable(),
});

export const ShareTokenDtoSchema = z.object({
  shareToken: z.string(),
  shareUrl: z.string(),
});

export const RoundVerificationStates = {
  Pending: 'pending',
  Success: 'success',
  Failed: 'failed',
  Processing: 'processing',
} as const;

const RoundVerificationSchema = z.object({
  id: z.string().uuid(),
  state: z.nativeEnum(RoundVerificationStates),
  transactionId: z.string().nullable(),
  transactionHash: z.string().nullable(),
  proofCount: z.number(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
});

export const RoundWithWinDtoSchema = z.object({
  id: z.string().uuid(),
  externalRoundId: z.string().nullable(),
  userId: z.string().uuid(),
  partnerId: z.string().uuid().nullable(),
  providerId: z.string().uuid().nullable(),
  gameId: z.string().uuid(),
  sessionId: z.string().uuid().nullable(),
  currency: z.enum(['GC', 'SC', 'BSC']),
  isFinished: z.boolean(),
  totalBet: z.number().nullable(),
  totalWin: z.number().nullable(),
  multiplier: z.number().nullable(),
  winId: z.string().nullable(),
  verificationId: z.string().nullable(),
  verification: RoundVerificationSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const SharedRoundDtoSchema = z.object({
  game: SharedGameSummarySchema,
  currency: z.enum(['GC', 'SC']),
  totalBet: z.number().nullable(),
  totalWin: z.number().nullable(),
  multiplier: z.number().nullable(),
  winId: z.string().nullable().optional(),
  verification: RoundVerificationSchema,
  createdAt: z.string(),
});

export const GameRtpResponseDtoSchema = z.object({
  currency: z.enum([Currency.SC, Currency.BSC, Currency.GC]).nullable(),
  endsAt: z.string(),
  gameId: z.string(),
  partnerId: z.string(),
  rtp: z.number(),
  startsAt: z.string(),
  totalBetAmount: z.number(),
  totalWinAmount: z.number(),
});

export const GameUserRtpResponseDtoSchema = GameRtpResponseDtoSchema.merge(
  z.object({
    userId: z.string(),
  }),
);
export const GameProviderRtpResponseDtoSchema = GameRtpResponseDtoSchema.merge(
  z.object({
    providerId: z.string(),
  }),
);

export const ReportPayloadDtoSchema = z.object({
  currency: z.enum([Currency.SC, Currency.GC]),
  startsAt: z.string(),
  endsAt: z.string(),
});

export const ReportResponseDtoSchema = z.object({
  correlationId: z.string(),
});

export const RoundsWithWinsParamsSchema = PaginationParamsSchema();

export type RoundsWithWinsParams = z.infer<typeof RoundsWithWinsParamsSchema>;
export type GameLastWinResponse = z.infer<typeof GameLastWinDtoSchema>;
export type ReportPayloadDto = z.infer<typeof ReportPayloadDtoSchema>;
export type ReportResponseDto = z.infer<typeof ReportResponseDtoSchema>;
export type GameUserRtpResponse = z.infer<typeof GameUserRtpResponseDtoSchema>;
export type GameProviderRtpResponse = z.infer<typeof GameProviderRtpResponseDtoSchema>;
export type ShareTokenDto = z.infer<typeof ShareTokenDtoSchema>;
export type SharedRoundDto = z.infer<typeof SharedRoundDtoSchema>;
export type RoundWithWinDto = z.infer<typeof RoundWithWinDtoSchema>;
